/**
 * NewCorrectionModal — start an Earnings Correction and save it as a DRAFT.
 * Nothing here changes a figure: applying is a separate, confirmed step.
 *
 * - From DS pipeline: pick months and a ChannelMeter payee (and/or channel
 *   ids); the server compares what ChannelMeter paid with what the pipeline
 *   computes today and proposes lines to review.
 * - Manual: pick ChannelMeter rows and type their corrected values.
 */

import React, { useMemo, useState } from 'react';
import { Button, Modal, Tabs } from '../../ui';
import {
  createEarningsCorrection,
  proposeDirectSoldCorrections,
  CorrectionDraftInput,
  DsProposal,
  EarningsCorrection,
  ProposalMonth,
} from '../../../services/earningsCorrectionsApi';
import {
  buildManualDraft,
  buildProposalDraft,
  CLIENT_NOTE_MAX,
  correctionErrorMessage,
  currentMonth,
  DraftFields,
  formatSignedCents,
  initialSelection,
  isCorrectableChannelId,
  ManualLineDraft,
  manualDeltaCents,
  monthsBetween,
  normalizeClientNoteInput,
  previousMonth,
  proposalLineKey,
  summarizeSelection,
  validateDraftFields,
} from './correctionModel';
import { cs, deltaColor, noticeStyle } from './correctionStyles';
import ProposalReview from './ProposalReview';
import ManualLinesEditor from './ManualLinesEditor';

type Mode = 'pipeline' | 'manual';

interface NewCorrectionModalProps {
  onClose: () => void;
  onCreated: (correction: EarningsCorrection) => void;
}

const MAX_PROPOSAL_MONTHS = 12;

const NewCorrectionModal: React.FC<NewCorrectionModalProps> = ({ onClose, onCreated }) => {
  const lastMonth = previousMonth(currentMonth());
  const [mode, setMode] = useState<Mode>('pipeline');
  const [fields, setFields] = useState<DraftFields>({ title: '', reason: '', clientNote: '' });

  // From DS pipeline
  const [fromMonth, setFromMonth] = useState(lastMonth);
  const [toMonth, setToMonth] = useState(lastMonth);
  const [payee, setPayee] = useState('');
  const [channelIdsText, setChannelIdsText] = useState('');
  const [proposal, setProposal] = useState<DsProposal | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [proposing, setProposing] = useState(false);
  const [proposalError, setProposalError] = useState<string | null>(null);

  // Manual
  const [manualDrafts, setManualDrafts] = useState<ManualLineDraft[]>([]);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const months = monthsBetween(fromMonth, toMonth);
  const channelIds = channelIdsText
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const badIds = channelIds.filter((c) => !isCorrectableChannelId(c));

  const buildProposal = async () => {
    setProposalError(null);
    if (months.length === 0) return setProposalError('Pick a start month on or before the end month.');
    if (months.length > MAX_PROPOSAL_MONTHS) return setProposalError(`Pick at most ${MAX_PROPOSAL_MONTHS} months.`);
    if (!payee.trim() && channelIds.length === 0) return setProposalError('Pick a ChannelMeter payee, or list channel ids.');
    if (badIds.length) return setProposalError(`Not YouTube channel ids: ${badIds.join(', ')}`);
    setProposing(true);
    try {
      const { proposal: p } = await proposeDirectSoldCorrections({
        months,
        ...(payee.trim() ? { user: payee.trim() } : {}),
        ...(channelIds.length ? { channelIds } : {}),
      });
      setProposal(p);
      setSelected(initialSelection(p));
      if (!fields.title.trim()) {
        const who = payee.trim() || `${channelIds.length} channel(s)`;
        setFields((f) => ({ ...f, title: `${who}: Direct Sold restated after GAM re-upload` }));
      }
    } catch (err) {
      setProposal(null);
      setProposalError(correctionErrorMessage(err, 'Failed to build the proposal.'));
    } finally {
      setProposing(false);
    }
  };

  const toggle = (key: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  const toggleMonth = (month: ProposalMonth, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      month.lines.forEach((line, i) => {
        if (!line.input) return;
        const key = proposalLineKey(month.month, i);
        if (on) next.add(key);
        else next.delete(key);
      });
      return next;
    });

  const selection = useMemo(() => (proposal ? summarizeSelection(proposal, selected) : null), [proposal, selected]);
  const manualNet = useMemo(
    () => manualDrafts.reduce<number | null>((sum, d) => {
      const delta = manualDeltaCents(d);
      return sum == null || delta == null ? null : sum + delta;
    }, 0),
    [manualDrafts],
  );

  const noteLength = normalizeClientNoteInput(fields.clientNote)?.length ?? 0;

  const save = async () => {
    setSaveError(null);
    const fieldError = validateDraftFields(fields);
    if (fieldError) return setSaveError(fieldError);
    let draft: CorrectionDraftInput;
    if (mode === 'pipeline') {
      if (!proposal) return setSaveError('Build a proposal first.');
      draft = buildProposalDraft(proposal, selected, fields);
      if (draft.lines.length === 0) return setSaveError('Select at least one line.');
    } else {
      const built = buildManualDraft(manualDrafts, fields);
      if (built.errors.length) return setSaveError(`Fix ${built.errors.length} line(s) first.`);
      if (!built.draft) return setSaveError('Add at least one line.');
      draft = built.draft;
    }
    setSaving(true);
    try {
      const { correction } = await createEarningsCorrection(draft);
      onCreated(correction);
    } catch (err) {
      setSaveError(correctionErrorMessage(err, 'Failed to save the draft.'));
      setSaving(false);
    }
  };

  const footerSummary =
    mode === 'pipeline'
      ? selection
        ? (
          <span>
            {selection.lines} line(s) selected · net{' '}
            <strong style={{ color: deltaColor(selection.deltaEarningsCents) }}>{formatSignedCents(selection.deltaEarningsCents)}</strong>
            {selection.reviewed ? ` · ${selection.reviewed} reviewed by hand` : ''}
          </span>
        )
        : null
      : manualDrafts.length
        ? (
          <span>
            {manualDrafts.length} line(s) · net{' '}
            {manualNet == null ? '—' : <strong style={{ color: deltaColor(manualNet) }}>{formatSignedCents(manualNet)}</strong>}
          </span>
        )
        : null;

  return (
    <Modal
      open
      onClose={saving ? () => {} : onClose}
      eyebrow="Earnings Corrections"
      title="New correction"
      width="min(1120px, 96vw)"
      closeOnBackdrop={false}
      closeOnEsc={false}
      footer={
        <div style={{ ...cs.row, justifyContent: 'space-between', width: '100%' }}>
          <div style={{ fontSize: 12.5, color: 'var(--fg-2)' }}>{footerSummary}</div>
          <div style={cs.row}>
            <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save draft'}
            </Button>
          </div>
        </div>
      }
    >
      <div style={cs.stack}>
        <Tabs<Mode>
          value={mode}
          onChange={(m) => {
            setMode(m);
            setSaveError(null);
          }}
          variant="segmented"
          size="sm"
          options={[
            { value: 'pipeline', label: 'From DS pipeline' },
            { value: 'manual', label: 'Manual' },
          ]}
        />

        {mode === 'pipeline' ? (
          <>
            <div style={cs.small}>
              Compares what ChannelMeter paid with what the Direct Sold pipeline computes today from the stored GAM upload,
              the current rate card and the stored channel mapping. Nothing is saved until you save the draft.
            </div>
            <div style={{ ...cs.kvGrid, alignItems: 'end' }}>
              <div>
                <label style={cs.label} htmlFor="nc-from">From</label>
                <input id="nc-from" type="month" style={cs.input} value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} />
              </div>
              <div>
                <label style={cs.label} htmlFor="nc-to">To</label>
                <input id="nc-to" type="month" style={cs.input} value={toMonth} onChange={(e) => setToMonth(e.target.value)} />
              </div>
              <div>
                <label style={cs.label} htmlFor="nc-payee">ChannelMeter payee</label>
                <input
                  id="nc-payee"
                  style={cs.input}
                  placeholder="Exact payee name, e.g. Goal"
                  value={payee}
                  onChange={(e) => setPayee(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label style={cs.label} htmlFor="nc-channels">Extra channel ids (optional)</label>
              <input
                id="nc-channels"
                style={cs.input}
                placeholder="UC… ids, separated by commas or spaces"
                value={channelIdsText}
                onChange={(e) => setChannelIdsText(e.target.value)}
              />
              <div style={cs.help}>Added to the payee’s own channels. Use these alone to scope by channel.</div>
            </div>
            <div style={cs.row}>
              <Button variant="subtle" size="sm" onClick={buildProposal} disabled={proposing}>
                {proposing ? 'Building proposal…' : proposal ? 'Rebuild proposal' : 'Build proposal'}
              </Button>
              {proposal && (
                <span style={cs.small}>
                  Built {new Date(proposal.generatedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} ·{' '}
                  {proposal.totals.lines} line(s), {proposal.totals.selectedLines} pre-selected
                </span>
              )}
            </div>
            {proposalError && (
              <div style={noticeStyle('danger')} role="alert">
                {proposalError}
              </div>
            )}
            {proposal && <ProposalReview proposal={proposal} selected={selected} onToggle={toggle} onToggleMonth={toggleMonth} />}
          </>
        ) : (
          <ManualLinesEditor drafts={manualDrafts} onChange={setManualDrafts} defaultMonth={lastMonth} />
        )}

        <div style={{ ...cs.section, padding: 16 }}>
          <div style={cs.stack}>
            <div>
              <label style={cs.label} htmlFor="nc-title">Title</label>
              <input
                id="nc-title"
                style={cs.input}
                maxLength={120}
                value={fields.title}
                onChange={(e) => setFields({ ...fields, title: e.target.value })}
                placeholder="e.g. Goal/Footballco: Jun–Aug 2026 GAM re-upload"
              />
            </div>
            <div>
              <label style={cs.label} htmlFor="nc-reason">Reason (internal)</label>
              <textarea
                id="nc-reason"
                style={cs.textarea}
                maxLength={2000}
                value={fields.reason}
                onChange={(e) => setFields({ ...fields, reason: e.target.value })}
                placeholder="What happened and where the new figures come from. Never shown to clients."
              />
            </div>
            <div>
              <label style={cs.label} htmlFor="nc-note">Client note (optional)</label>
              <input
                id="nc-note"
                style={cs.input}
                value={fields.clientNote}
                onChange={(e) => setFields({ ...fields, clientNote: e.target.value })}
                placeholder="Shown under the table on client report pages"
              />
              <div style={{ ...cs.help, color: noteLength > CLIENT_NOTE_MAX ? 'var(--danger)' : undefined }}>
                {noteLength}/{CLIENT_NOTE_MAX}. Leave it empty for the standard wording, e.g. “Updated Sep 30, 2026: Direct Sold
                for Jun–Aug 2026 was restated after a delivery data correction.” It’s the only part of a correction a client ever sees.
              </div>
            </div>
          </div>
        </div>

        {saveError && (
          <div style={noticeStyle('danger')} role="alert">
            {saveError}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default NewCorrectionModal;
